import React, { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { api } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";

type Status = "checking" | "unsupported" | "off" | "on" | "denied" | "busy";

const isNative = Capacitor.isNativePlatform();
// @capacitor/push-notifications hands back a raw APNs device token on iOS,
// not an FCM one — fcm.ts's send path only understands FCM tokens. iOS uses
// @capacitor-firebase/messaging instead, which does the APNs<->FCM token
// exchange internally, so both platforms end up posting an FCM token to the
// same /api/push/subscribe endpoint and going through the same server-side
// send path (see push.ts). Mixing both plugins on the SAME platform risks a
// double permission prompt / conflicting delegate, so each platform uses
// exactly one of them, never both.
const isIOS = Capacitor.getPlatform() === "ios";

// VAPID public keys are base64url — the Push API wants them as a raw
// Uint8Array, so decode by hand rather than pulling in a base64 library.
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64Safe = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64Safe);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

// The last FCM token this device registered — kept so disable() has
// something to tell the server to drop. Also updated by the persistent
// listener below, which re-syncs the server on Firebase's own periodic
// token refreshes, not just the first registration.
let lastNativeToken: string | null = null;
// Android 8+ (API 26+) groups notifications into channels and lets the user
// (or the OEM) mute/downgrade one independently of the app's own permission
// grant. Without an app-created channel, FCM's SDK falls back to a generic
// channel it creates for itself with default (not high) importance on some
// OEM skins — the notification is technically delivered but never shows a
// heads-up alert or sound, which reads to a user as "notifications aren't
// working" even though the send pipeline is fine. Server side, fcm.ts
// targets this exact channel id.
const DEFAULT_CHANNEL_ID = "toletbook_default";
if (isNative && isIOS) {
  // Fires on Firebase's own periodic token refreshes too, not just the
  // first registration — keeps the server's copy current the same way the
  // Android 'registration' listener below does.
  FirebaseMessaging.addListener("tokenReceived", (event) => {
    lastNativeToken = event.token;
    api.post("/api/push/subscribe", { fcmToken: event.token, platform: "ios" }).catch(() => {});
  });
} else if (isNative) {
  PushNotifications.createChannel({
    id: DEFAULT_CHANNEL_ID,
    name: "General",
    description: "Rent reminders, requests, visits and other account alerts",
    importance: 5,
    visibility: 1,
  }).catch(() => {});
  PushNotifications.addListener("registration", (token) => {
    lastNativeToken = token.value;
    api.post("/api/push/subscribe", { fcmToken: token.value, platform: "android" }).catch(() => {});
  });
}

// The Push API (Notification/serviceWorker/PushManager) doesn't work inside
// the Capacitor app's WebView — the native app registers a real FCM device
// token instead, delivered via the native Android/iOS notification system
// rather than a browser push subscription. See fcm.ts on the server.
async function subscribeToPushNative(): Promise<"on" | "denied" | "off"> {
  if (isIOS) {
    let perm = await FirebaseMessaging.checkPermissions();
    if (perm.receive !== "granted" && perm.receive !== "denied") {
      perm = await FirebaseMessaging.requestPermissions();
    }
    if (perm.receive === "denied") return "denied";
    if (perm.receive !== "granted") return "off";
    try {
      // Unlike Android's fire-and-forget register(), getToken() resolves
      // with the token directly — post it here instead of only relying on
      // the tokenReceived listener above, so the very first subscribe
      // doesn't race that listener's async delivery.
      const { token } = await FirebaseMessaging.getToken();
      lastNativeToken = token;
      await api.post("/api/push/subscribe", { fcmToken: token, platform: "ios" });
      return "on";
    } catch {
      return "off";
    }
  }
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive !== "granted" && perm.receive !== "denied") {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive === "denied") return "denied";
  if (perm.receive !== "granted") return "off";
  try {
    await PushNotifications.register();
    // The token itself arrives asynchronously via the 'registration'
    // listener above (which also posts it to the server) — granted
    // permission plus a successful register() call is as far as this
    // function can confirm synchronously without duplicating that listener.
    return "on";
  } catch {
    return "off";
  }
}

// Shared by the manual Settings toggle and the once-per-device auto-prompt
// below — both end up doing the exact same permission request + subscribe
// dance, just triggered differently.
export async function subscribeToPush(): Promise<"on" | "denied" | "off" | "unsupported"> {
  if (isNative) return subscribeToPushNative();
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    return "unsupported";
  }
  if (Notification.permission !== "granted") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return perm === "denied" ? "denied" : "off";
  }
  try {
    const { vapidPublicKey } = await api.get("/api/config");
    if (!vapidPublicKey) return "off";
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
    });
    await api.post("/api/push/subscribe", sub.toJSON());
    return "on";
  } catch {
    return "off";
  }
}

// Fires the permission prompt automatically the first time a user's device
// ever loads the app, instead of leaving push opt-in buried in Settings —
// "by default" is as close as either the Push API or the native permission
// dialog allows, since only the OS/browser's own prompt can actually grant
// it. Guarded so it only ever asks once per device: a denial or dismissal
// is remembered just like an accept, so refreshing the page (or reopening
// the app) doesn't re-trigger the prompt.
const AUTO_PROMPT_KEY = "toletbook_push_auto_prompted";
export function useAutoEnablePush(signedIn = true) {
  useEffect(() => {
    if (!signedIn) return;
    if (isNative) {
      // Android 13+ needs the runtime permission, and a token must be
      // registered on every launch to stay in sync with the server. When the
      // permission is already granted this is silent; when it was never
      // decided the system dialog appears; a denial is left alone (the
      // Settings toggle explains how to re-enable it).
      const timer = setTimeout(() => {
        subscribeToPush().catch(() => {});
      }, 1500);
      return () => clearTimeout(timer);
    }
    if (localStorage.getItem(AUTO_PROMPT_KEY)) return;
    if (!("Notification" in window) || Notification.permission !== "default") {
      localStorage.setItem(AUTO_PROMPT_KEY, "1");
      return;
    }
    const timer = setTimeout(() => {
      localStorage.setItem(AUTO_PROMPT_KEY, "1");
      subscribeToPush();
    }, 1500);
    return () => clearTimeout(timer);
  }, [signedIn]);
}

// checkPermissions() is a real native-bridge round trip on Android — slow
// enough on some devices to read as a visible stutter (toggle stuck
// disabled in "checking" state) every time Settings is opened. Caching the
// last known result means only the very first check in a session pays that
// cost; every later mount renders the last-known state immediately and
// re-verifies quietly in the background (still catching a permission the
// user changed from system settings, just without blocking the repaint).
let cachedStatus: Status | null = null;

export function NotificationsMenuItem() {
  const { t } = useLanguage();
  const toast = useToast();
  const [status, setStatus] = useState<Status>(cachedStatus ?? "checking");

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (isNative) {
        const perm = isIOS ? await FirebaseMessaging.checkPermissions() : await PushNotifications.checkPermissions();
        const resolved = perm.receive === "granted" ? "on" : perm.receive === "denied" ? "denied" : "off";
        if (!cancelled) setStatus(resolved);
        cachedStatus = resolved;
        return;
      }
      if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
        setStatus("unsupported");
        cachedStatus = "unsupported";
        return;
      }
      if (Notification.permission === "denied") {
        setStatus("denied");
        cachedStatus = "denied";
        return;
      }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        const resolved = sub ? "on" : "off";
        if (!cancelled) setStatus(resolved);
        cachedStatus = resolved;
      } catch {
        if (!cancelled) setStatus("off");
        cachedStatus = "off";
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  async function enable() {
    setStatus("busy");
    const result = await subscribeToPush();
    if (result === "on") {
      setStatus("on");
      cachedStatus = "on";
      toast(t("notifications.enabledToast"));
    } else if (result === "denied") {
      setStatus("denied");
      cachedStatus = "denied";
    } else if (result === "unsupported") {
      setStatus("unsupported");
      cachedStatus = "unsupported";
    } else {
      toast(t("notifications.couldNotEnableToast"));
      setStatus("off");
      cachedStatus = "off";
    }
  }

  async function disable() {
    setStatus("busy");
    try {
      if (isNative) {
        if (isIOS) {
          await FirebaseMessaging.deleteToken();
        } else {
          await PushNotifications.unregister();
        }
        if (lastNativeToken) {
          await api.post("/api/push/unsubscribe", { fcmToken: lastNativeToken }).catch(() => {});
          lastNativeToken = null;
        }
      } else {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api.post("/api/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
          await sub.unsubscribe();
        }
      }
      setStatus("off");
      cachedStatus = "off";
      toast(t("notifications.disabledToast"));
    } catch {
      setStatus("on");
      cachedStatus = "on";
    }
  }

  if (status === "unsupported") return null;

  return (
    <>
      <div className="menu-item">
        <div className="menu-icon blue">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </div>
        <span className="mi-label">{t("notifications.title")}</span>
        <span className="mi-right">
          <button
            type="button"
            className={`toggle${status === "on" ? " on" : ""}`}
            disabled={status === "busy" || status === "checking" || status === "denied"}
            onClick={() => (status === "on" ? disable() : enable())}
            aria-label={t("notifications.title")}
          >
            <i />
          </button>
        </span>
      </div>
      {status === "denied" && (
        <div className="field-hint" style={{ padding: "0 14px 14px" }}>{t("notifications.deniedHint")}</div>
      )}
    </>
  );
}
